import { z } from "zod";

import { McpToolSchemaHashSchema } from "../../../../schemas/McpServerTool/schemaHash.schema";

/**
 * Attaching one approved MCP tool to one agent.
 *
 * ## The body names the AGENT and nothing about the tool
 *
 * The server and the tool are in the path, and everything the skill needs to
 * dispatch — which tool, under which name, at which contract — is READ from the
 * approved row rather than supplied. A caller cannot name the action, cannot
 * name the schema and cannot name the hash, so there is no field here through
 * which a skill could be attached to one tool and pointed at another.
 *
 * ## No `schemaHash`, unlike approval, and the asymmetry is the point
 *
 * `ApproveMcpServerToolBodySchema` takes the hash the administrator was shown,
 * because approval is the act that BLESSES a contract and must refuse to bless
 * one nobody read. Attaching blesses nothing: it binds to whatever an
 * administrator has ALREADY approved, and the hash it records is read from the
 * row in the same breath. Asking for one here would invite a caller to pin a
 * hash the row does not carry, which is a pin that is broken from birth.
 */
export const AttachMcpToolBodySchema = z.object({
  /** The agent to give the skill to. Must belong to the caller's organization. */
  agentId: z.string().uuid(),
  /**
   * The operator's own name for the skill. Defaults to the tool's own name.
   *
   * Not guaranteed to be the label the row gets: `AgentToolConfig` is unique on
   * `(agentId, label)`, so a collision is suffixed. The response carries the
   * label that was actually written.
   */
  label: z.string().min(1).max(200).optional(),
  /**
   * A `ToolCredentials` row bound to this server, when the caller selected one.
   *
   * Optional because it genuinely is: a server whose `authMode` is NONE carries
   * no credential, and one that does can fall back to the organization's default
   * for the tool at dispatch.
   */
  toolCredentialId: z.string().uuid().optional()
});

export type AttachMcpToolBody = z.infer<typeof AttachMcpToolBodySchema>;

/**
 * The skill as attached.
 *
 * Deliberately the same field names as `McpBrokenToolPinSchema` where the two
 * overlap, because they describe one thing at two moments — a surface that
 * attaches a skill and then polls the broken list should not have to translate
 * between two spellings of `agentToolConfigId`.
 *
 * It carries no contract and no schema. The contract is the tool's, it is large,
 * and the caller can read it from the server detail view it just came from;
 * repeating it here would be a second copy of a hashed artifact on a write
 * response.
 */
export const McpAttachedSkillSchema = z.object({
  /** `AgentToolConfig.id` — the skill that now exists. */
  agentToolConfigId: z.string().uuid(),
  agentId: z.string().uuid(),
  /** The label the row ACTUALLY got, suffixed if the requested one collided. */
  label: z.string(),
  /** `AgentExternalTool.id`, which IS `McpServer.externalToolId`. */
  serverId: z.string().uuid(),
  toolId: z.string().uuid(),
  /** The server's own name for the tool. Server-authored: render as text. */
  toolName: z.string(),
  /**
   * The contract this skill is bound to. Dispatch refuses unless the tool is
   * still APPROVED at exactly this hash, so it is the drift anchor rather than a
   * decoration — a surface can compare it against the tool's current
   * `approvedSchemaHash` to show a skill going stale.
   */
  pinnedSchemaHash: McpToolSchemaHashSchema
});

export type McpAttachedSkill = z.infer<typeof McpAttachedSkillSchema>;
